import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../../framework/BaseWebComponentController';
import { LicenseMonthlyAddonsFormWebComponent } from '../component/LicenseMonthlyAddonsFormWebComponent';
import { isHidden, toggleVisibility } from '../../../../utils/htmlElement.utils';
import { ToggleEvents } from '../../../../ui/gzToggle/types';
import { LicenseMonthlyAddonsFormView } from '../view/LicenseMonthlyAddonsFormView';
import { Feature, FeatureFlag } from '@nirvana/shared/constants';
import { AddonFormEvent } from '../types/AddonFormEvent';
import { AddonFormType } from '../types/AddonFormType';
import { AddonFieldComponents } from '../types/AddonFieldComponentsMap';
import { MonthlyProductType } from '../../../types/MonthlyProductType';
import _ from 'lodash';
import { CompanyType } from '../../../../company/types/company-type';
import { CompanyFeatures } from '../../../../company/types/company-data';
import { templateLicenseMonthlyAddonsForm } from '../view/template';
import { LicenseConstants } from '../../../LicenseConstants';
import { htmlEncode } from '../../../../utils/escape.utils';
import { GzHardCodedFormField } from '../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { RadioEvent } from '../../../../ui/gzRadio/types';
import { Bundle } from '../../../types/Bundles';

export class LicenseMonthlyAddonsFormController extends BaseWebComponentController {
  public component: LicenseMonthlyAddonsFormWebComponent;
  public view: LicenseMonthlyAddonsFormView;
  public viewConstructor: typeof LicenseMonthlyAddonsFormView;

  private childCompanyType: CompanyType;
  private usableProduct: MonthlyProductType | undefined;
  private delegableProducts: MonthlyProductType[] | undefined;
  private availableFeatures: Feature[] | undefined;
  private bundleId: Bundle | undefined;

  private imES90daysStorageFormField: GzHardCodedFormField<boolean> = new GzHardCodedFormField<boolean>(false);
  private imES180daysStorageFormField: GzHardCodedFormField<boolean> = new GzHardCodedFormField<boolean>(false);
  private imES365daysStorageFormField: GzHardCodedFormField<boolean> = new GzHardCodedFormField<boolean>(false);

  private edrES90daysStorageFormField: GzHardCodedFormField<boolean> = new GzHardCodedFormField<boolean>(false);
  private edrES180daysStorageFormField: GzHardCodedFormField<boolean> = new GzHardCodedFormField<boolean>(false);
  private edrES365daysStorageFormField: GzHardCodedFormField<boolean> = new GzHardCodedFormField<boolean>(false);

  // The form can be own use or resell
  private formType: AddonFormType;

  private isTrial = false;
  private isFormHidden = true;
  private isFormReadOnly = true;
  private featuresInTrial: FeatureFlag[];

  constructor(component: LicenseMonthlyAddonsFormWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: LicenseMonthlyAddonsFormView,
      viewTemplate: templateLicenseMonthlyAddonsForm,
    });
  }

  async start() {
    await super.start();
    this.initAddonListeners();
  }

  public setChildCompanyType(childCompanyType: CompanyType): void {
    this.childCompanyType = childCompanyType;
  }

  public setUsableProduct(usableProduct: MonthlyProductType | undefined): void {
    this.usableProduct = usableProduct;

    this.showCompatibleAddons();
    this.updateAllAddonFieldsComponents();
    this.updateIntegrityMonitorRetention();
    this.toggleFormEditability();
  }

  public setDelegableProducts(delegableProducts: MonthlyProductType[] | undefined): void {
    this.delegableProducts = delegableProducts;

    this.showCompatibleAddons();
    this.updateAllAddonFieldsComponents();
  }

  public setAvailableFeatures(availableFeatures: Feature[] | undefined): void {
    this.availableFeatures = availableFeatures;

    this.showCompatibleAddons();
  }

  public setFeaturesInTrial(featuresInTrial: FeatureFlag[] | undefined): void {
    this.featuresInTrial = featuresInTrial ? featuresInTrial : [];
  }

  public setFormType(formType: AddonFormType): void {
    this.formType = formType;
    if (this.formType === AddonFormType.Resell) {
      this.featuresInTrial = [];
    }

    this.showCompatibleAddons();
    this.toggleFormEditability();
  }

  public setIsTrial(isTrial: boolean): void {
    this.isTrial = isTrial;
  }

  public setBundleId(bundleId: Bundle): void {
    this.bundleId = bundleId;
  }

  /**
   * Set EDR's value and editability when MDR toggle changes value.
   * @param {boolean} mdrIsActivated
   */
  public updateEdrForMdr(mdrIsActivated: boolean): void {
    if (!this.isMainForm() && this.usableProduct !== MonthlyProductType.EndpointSecurity) {
      // EDR's value will be updated indirectly, via events, if necessary.
      return;
    }

    if (mdrIsActivated && !this.view.addonFields.endpointDetectionAndResponse.$uiToggle.value) {
      // Prevent event generation when setting the value to true when it's already true
      this.view.addonFields.endpointDetectionAndResponse.$uiToggle.value = true;
    }

    const isEdrInTrial = this.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE];

    if (this.formType === AddonFormType.Resell) {
      this.view.addonFields.endpointDetectionAndResponse.$uiToggle.disabled = mdrIsActivated || isEdrInTrial;
      return;
    }

    this.view.addonFields.endpointDetectionAndResponse.$uiToggle.disabled =
      mdrIsActivated ||
      isEdrInTrial ||
      this.bundleId !== Bundle.PRODUCT_MSP_BUNDLE_A_LA_CARTE ||
      this.usableProduct === MonthlyProductType.BitdefenderEdr;
  }

  public updateToggles(features: CompanyFeatures) {
    this.setUsableProduct(MonthlyProductType.getAssignedProduct(features));

    for (const featureFlag of Object.keys(features)) {
      const featureId = Feature.getFeatureId(featureFlag);
      const addonField = Object.values(this.view.addonFields).find(addon => {
        if (this.formType === AddonFormType.OwnUse) {
          return addon.usableFeature === featureId;
        } else {
          return addon.delegateFeature === featureId;
        }
      });

      if (!addonField) {
        continue;
      }
      addonField.$uiToggle.value = features[featureFlag];

      if (this.formType === AddonFormType.OwnUse && addonField.inTrialLabel && this.featuresInTrial) {
        toggleVisibility(addonField.inTrialLabel, this.featuresInTrial[featureFlag]);
        addonField.$uiToggle.disabled = this.featuresInTrial[featureFlag];
        if (featureFlag === FeatureFlag.PRODUCT_FEATURE_EDR_USABLE) {
          this.onEndpointDetectionAndResponseToggle();
        }
      }
    }

    if (this.featuresInTrial && this.formType === AddonFormType.OwnUse) {
      const isAtsInTrial =
        this.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_ADVANCED_THREAT_PREVENTION_USABLE] &&
        this.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_SANDBOX_ANALYZER_USABLE];
      toggleVisibility(this.view.dom.$atsInTrialText, isAtsInTrial);
      const isEdrInTrial = this.featuresInTrial[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE];
      this.view.dom.$extendedDetectionAndResponseToggle.disabled = isEdrInTrial;
      this.view.addonFields.identityProviders.$uiToggle.disabled = isEdrInTrial;
      this.view.addonFields.productivityApps.$uiToggle.disabled = isEdrInTrial;
      this.view.addonFields.cloudWorkloads.$uiToggle.disabled = isEdrInTrial;
      this.view.addonFields.networkSensors.$uiToggle.disabled = isEdrInTrial;
    }

    if (features.integrityMonitorUsable) {
      if (features.imES90daysStorage) {
        this.view.dom.$imRetentionAddons.setFormValue(FeatureFlag.PRODUCT_FEATURE_IM_ES_90DAYS_STORAGE);
      } else if (features.imES180daysStorage) {
        this.view.dom.$imRetentionAddons.setFormValue(FeatureFlag.PRODUCT_FEATURE_IM_ES_180DAYS_STORAGE);
      } else if (features.imES365daysStorage) {
        this.view.dom.$imRetentionAddons.setFormValue(FeatureFlag.PRODUCT_FEATURE_IM_ES_365DAYS_STORAGE);
      }
    }

    if (features.edrUsable) {
      if (features.edrES90daysStorage) {
        this.view.dom.$edrRetentionAddons.setFormValue(FeatureFlag.PRODUCT_FEATURE_EDR_ES_90DAYS_STORAGE);
      } else if (features.edrES180daysStorage) {
        this.view.dom.$edrRetentionAddons.setFormValue(FeatureFlag.PRODUCT_FEATURE_EDR_ES_180DAYS_STORAGE);
      } else if (features.edrES365daysStorage) {
        this.view.dom.$edrRetentionAddons.setFormValue(FeatureFlag.PRODUCT_FEATURE_EDR_ES_365DAYS_STORAGE);
      }
    }
  }

  /**
   * Update a toggle's value from the parent in the DOM. This method is necessary in keeping own use and resell forms
   * synchronized, except XDR toggle group.
   *
   * @param {string} toggle
   * @param {boolean} value
   */
  public updateToggle(toggle: string, value: boolean): void {
    if (toggle === this.view.addonFields.phasr.key || toggle === this.view.addonFields.easm.key) return;
    if (!this.view.xdrGroupAddonFields.has(toggle) && this.view.addonFields[toggle].$uiToggle.value !== value) {
      this.view.addonFields[toggle].$uiToggle.value = value;
    }
  }

  computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$advancedThreatSecurityToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onAdvancedThreatSecurityToggle,
      },
      {
        element: this.view.addonFields.hyperDetect.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onHyperDetectToggle,
      },
      {
        element: this.view.addonFields.sandboxAnalyzer.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onSandboxAnalyzerToggle,
      },
      {
        element: this.view.addonFields.endpointDetectionAndResponse.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onEndpointDetectionAndResponseToggle,
      },
      {
        element: this.view.dom.$extendedDetectionAndResponseToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onExtendedDetectAndResponseToggle,
      },
      {
        element: this.view.addonFields.identityProviders.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onIdentityProvidersToggle,
      },
      {
        element: this.view.addonFields.productivityApps.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onProductivityAppsToggle,
      },
      {
        element: this.view.addonFields.cloudWorkloads.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onCloudWorkloads,
      },
      {
        element: this.view.addonFields.networkSensors.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onNetworkSensors,
      },
      {
        element: this.view.addonFields.integrityMonitor.$uiToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onIntegrityMonitorToggleChange,
      },
      {
        element: this.view.dom.$imRetentionAddons,
        event: RadioEvent.SELECTION_CHANGED,
        callback: this.onIntegrityMonitorRetentionSelection,
      },
      {
        element: this.view.dom.$edrRetentionAddons,
        event: RadioEvent.SELECTION_CHANGED,
        callback: this.onEdrRetentionSelectionChanged,
      },
    ];
  }

  onEdrRetentionSelectionChanged(event) {
    this.edrES90daysStorageFormField.value = false;
    this.edrES180daysStorageFormField.value = false;
    this.edrES365daysStorageFormField.value = false;

    switch (event.detail.selectedValue) {
      case FeatureFlag.PRODUCT_FEATURE_EDR_ES_90DAYS_STORAGE:
        this.edrES90daysStorageFormField.value = true;
        return;

      case FeatureFlag.PRODUCT_FEATURE_EDR_ES_180DAYS_STORAGE:
        this.edrES180daysStorageFormField.value = true;
        return;

      case FeatureFlag.PRODUCT_FEATURE_EDR_ES_365DAYS_STORAGE:
        this.edrES365daysStorageFormField.value = true;
        return;
    }
  }

  /**
   * Initialize the rest of the listeners (for the addons).
   */
  private initAddonListeners(): void {
    for (const addonField of Object.values(this.view.addonFields)) {
      addonField.$uiToggle.addEventListener(ToggleEvents.TOGGLE_CHANGE, () => {
        this.onAddonToggle(addonField);
      });
    }
  }

  /**
   * Toggle visibility for every addon and for the form itself.
   */
  private showCompatibleAddons(): void {
    let isAnyToggleAvailable = false;

    for (const addonField of Object.values(this.view.addonFields)) {
      isAnyToggleAvailable = this.showSingleCompatibleAddon(addonField) || isAnyToggleAvailable;
    }

    // ATS is a "toggle group"-like toggle. It doesn't have an associated feature flag, but controls HyperDetect and
    // Sandbox Analyzer
    this.setAtsToggle();

    // XDR is a "toggle group"-like toggle. It doesn't have an associated feature flag, but it controls Identity Providers, Cloud workloads, Productivity Apps and Network sensors
    this.setXDRToggle();
    this.hideXDRSensorsForResell();
    this.setXDRInfo();

    this.toggleIntegrityMonitorRetentionVisibility();
    this.toggleEDRESRetentionStorageVisibility();

    this.isFormHidden = !isAnyToggleAvailable;

    const usableProduct = this.usableProduct;

    const formType = this.formType;
    this.component.dispatchEvent(
      new CustomEvent(AddonFormEvent.ViewUpdate, {
        detail: {
          formType,
          isAnyToggleAvailable,
          usableProduct,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private showSingleCompatibleAddon(addonField: AddonFieldComponents): boolean {
    const isAddonAvailable = this.isAddonAvailable(addonField);
    let usableOrDelegateFlag: string;

    if (this.formType === AddonFormType.OwnUse) {
      usableOrDelegateFlag = Feature.getFeatureFlag(addonField.usableFeature);
    } else {
      usableOrDelegateFlag = Feature.getFeatureFlag(addonField.delegateFeature);
    }

    // Add feature in specifics.feature
    this.view.formGroup.addFormChild([usableOrDelegateFlag], addonField.memoryToggle);

    toggleVisibility(addonField.$uiToggle, isAddonAvailable);

    return isAddonAvailable;
  }

  public isAddonAvailable(addonField: AddonFieldComponents): boolean {
    const hasRequiredFeature = Feature.isFeatureAvailable(this.availableFeatures || [], addonField.delegateFeature);
    const canUseOrDelegateAddon = this.canUseOrDelegateAddon(addonField);

    return hasRequiredFeature && canUseOrDelegateAddon;
  }

  private hideXDRSensorsForResell() {
    if (this.formType === AddonFormType.Resell) {
      toggleVisibility(this.view.addonFields.identityProviders.$uiToggle, false);
      toggleVisibility(this.view.addonFields.productivityApps.$uiToggle, false);
      toggleVisibility(this.view.addonFields.cloudWorkloads.$uiToggle, false);
      toggleVisibility(this.view.addonFields.networkSensors.$uiToggle, false);
    }
  }

  private setAtsToggle(): void {
    // Advanced Threat Security ("group toggle" for HyperDetect and Sandbox)
    const shouldShowAtsToggle =
      !this.view.addonFields.hyperDetect.$uiToggle.classList.contains('hidden') ||
      !this.view.addonFields.sandboxAnalyzer.$uiToggle.classList.contains('hidden');

    toggleVisibility(this.view.dom.$advancedThreatSecurityToggle, shouldShowAtsToggle);
    if (!shouldShowAtsToggle) {
      toggleVisibility(this.view.dom.$endpointDetectionAndResponseText, false);
    }
  }

  private setXDRToggle(): void {
    // eXtended Detection and Response ("group toggle" for Identity Providers, Cloud workloads, Productivity Apps, Network sensors)
    const shouldShowXDRToggle =
      !this.view.addonFields.identityProviders.$uiToggle.classList.contains('hidden') ||
      !this.view.addonFields.productivityApps.$uiToggle.classList.contains('hidden') ||
      !this.view.addonFields.cloudWorkloads.$uiToggle.classList.contains('hidden') ||
      !this.view.addonFields.networkSensors.$uiToggle.classList.contains('hidden');

    toggleVisibility(this.view.dom.$extendedDetectionAndResponseToggle, shouldShowXDRToggle);
    toggleVisibility(this.view.dom.$ownUseXdrSensorsInfoSection, shouldShowXDRToggle);
  }

  private setXDRInfo() {
    let xdrSensorInfo =
      'The feature requires Advanced Threat Security and Endpoint Detection and Response. Once activated, you can set up your sensors from {bold}.';

    if (this.formType === AddonFormType.Resell) {
      xdrSensorInfo =
        'The add-on requires Advanced Threat Security and Endpoint Detection and Response. It includes access to integrating all the available sensor types: Identity providers, Productivity apps, Network, and Cloud workloads. Once activated, sensors can be configured from {bold}.';
    }

    this.view.dom.$ownUseXdrSensorsInfo.$infoText.innerHTML = this.dependencies.translateFn(xdrSensorInfo, {
      bold:
        '<span class="text-medium">' + this.dependencies.translateFn('Configuration > Sensor Management') + '</span>',
    });

    this.view.dom.$ownUseXdrSensorsInfo.$link.innerHTML = `<span>${htmlEncode(
      this.dependencies.translateFn('Learn more')
    )}</span>`;
    this.view.dom.$ownUseXdrSensorsInfo.$link.href = LicenseConstants.InfoLinks.XDRConfiguration;
  }

  private toggleIntegrityMonitorRetentionVisibility(): void {
    toggleVisibility(this.view.dom.$imRetentionAddons, false);

    if (
      !this.isTrial &&
      this.formType === AddonFormType.OwnUse &&
      this.availableFeatures?.includes(Feature.PRODUCT_FEATURE_INTEGRITY_MONITOR_USABLE_DELEGATE) &&
      this.usableProduct === MonthlyProductType.EndpointSecurity
    ) {
      toggleVisibility(this.view.dom.$imRetentionAddons, true);

      this.view.formGroup.addFormChild(
        [FeatureFlag.PRODUCT_FEATURE_IM_ES_90DAYS_STORAGE],
        this.imES90daysStorageFormField
      );
      this.view.formGroup.addFormChild(
        [FeatureFlag.PRODUCT_FEATURE_IM_ES_180DAYS_STORAGE],
        this.imES180daysStorageFormField
      );
      this.view.formGroup.addFormChild(
        [FeatureFlag.PRODUCT_FEATURE_IM_ES_365DAYS_STORAGE],
        this.imES365daysStorageFormField
      );
      return;
    }
  }

  /**
   * EDR retention options are shown only in Own Use form, if the partner can resell EDR.
   * For Resell form, the retention is included in EDR product.
   *
   * @private
   */
  private toggleEDRESRetentionStorageVisibility(): void {
    toggleVisibility(this.view.dom.$edrRetentionAddons, false);

    if (
      !this.isTrial &&
      this.formType === AddonFormType.OwnUse &&
      (
        this.availableFeatures?.includes(Feature.PRODUCT_FEATURE_EDR_USABLE_DELEGATE) ||
        this.usableProduct === MonthlyProductType.BitdefenderEdr
      )
    ) {
      toggleVisibility(this.view.dom.$edrRetentionAddons, true);

      this.view.formGroup.addFormChild(
        [FeatureFlag.PRODUCT_FEATURE_EDR_ES_90DAYS_STORAGE],
        this.edrES90daysStorageFormField
      );
      this.view.formGroup.addFormChild(
        [FeatureFlag.PRODUCT_FEATURE_EDR_ES_180DAYS_STORAGE],
        this.edrES180daysStorageFormField
      );
      this.view.formGroup.addFormChild(
        [FeatureFlag.PRODUCT_FEATURE_EDR_ES_365DAYS_STORAGE],
        this.edrES365daysStorageFormField
      );

      return;
    }
  }

  private toggleEdrTooltipVisibility(): void {
    const isToggleVisible = !this.view.addonFields.endpointDetectionAndResponse.$uiToggle.classList.contains('hidden');

    toggleVisibility(this.view.dom.$endpointDetectionAndResponseText, isToggleVisible);
  }

  private updateAllAddonFieldsComponents(): void {
    for (const addonField of Object.values(this.view.addonFields)) {
      this.updateSingleAddonFieldComponents(addonField);
    }
  }

  private updateIntegrityMonitorRetention(): void {
    const retentionAddon =
      this.view.addonFields.integrityMonitor.$uiToggle.value &&
      this.usableProduct === MonthlyProductType.EndpointSecurity
        ? this.view.imES7daysDummyFlag
        : '';
    this.view.dom.$imRetentionAddons.setFormValue(retentionAddon);
  }

  private updateSingleAddonFieldComponents(addonField: AddonFieldComponents): void {
    if (!addonField) {
      return;
    }

    const canUseOrDelegateAddon = this.canUseOrDelegateAddon(addonField);

    // Get value from the uiToggle if addon can be used / delegated
    const value = canUseOrDelegateAddon ? addonField.$uiToggle.value : false;
    if (addonField.memoryToggle.value !== value) {
      addonField.memoryToggle.value = value;
    }
  }

  private canUseOrDelegateAddon(addonField: AddonFieldComponents): boolean {
    if (this.formType === AddonFormType.OwnUse) {
      return this.usableProduct !== undefined && addonField.availableForProducts.includes(this.usableProduct);
    }

    if (this.formType === AddonFormType.Resell) {
      return _.intersection(this.delegableProducts, addonField.availableForProducts).length > 0;
    }

    return false;
  }

  /**
   * Toggle form as readonly if it the resell form for a company with Endpoint Security.
   */
  private toggleFormEditability(): void {
    const onResellWithEndpointSecurity =
      this.formType === AddonFormType.Resell && this.usableProduct === MonthlyProductType.EndpointSecurity;

    if (this.isFormReadOnly === onResellWithEndpointSecurity) {
      return;
    }

    this.isFormReadOnly = onResellWithEndpointSecurity;
    const isReadOnly = this.isFormReadOnly || this.view.addonFields.endpointDetectionAndResponse.$uiToggle.value;

    for (const addonField of Object.values(this.view.addonFields)) {
      if (onResellWithEndpointSecurity && this.view.allowedFeaturesToDelegateWithoutOwning.has(addonField.key)) {
        addonField.$uiToggle.disabled = false;
      } else {
        if (this.view.atsGroupAddonFields.has(addonField.key)) {
          addonField.$uiToggle.disabled = isReadOnly;
        } else {
          addonField.$uiToggle.disabled = this.isFormReadOnly;
        }
      }
    }
  }

  private onAdvancedThreatSecurityToggle(): void {
    const value = this.view.dom.$advancedThreatSecurityToggle.value;
    const isHyperDetectOn = this.view.addonFields.hyperDetect.$uiToggle.value;
    const isSandboxAnalyzerOn = this.view.addonFields.sandboxAnalyzer.$uiToggle.value;
    const shouldChangeGroup = !value || isHyperDetectOn === isSandboxAnalyzerOn;

    // ATS can be toggled directly or indirectly (when HD or SA are activated)
    // if ATS was toggled indirectly do not re-update the group
    if (shouldChangeGroup) {
      if (isHyperDetectOn !== value) {
        this.view.addonFields.hyperDetect.$uiToggle.value = value;
      }

      if (isSandboxAnalyzerOn !== value) {
        this.view.addonFields.sandboxAnalyzer.$uiToggle.value = value;
      }
    }
  }

  private onHyperDetectToggle(): void {
    this.updateAtsToggle();
  }

  private onSandboxAnalyzerToggle(): void {
    this.updateAtsToggle();
  }

  private updateAtsToggle(): void {
    const isHyperDetectOn = this.view.addonFields.hyperDetect.$uiToggle.value;
    const isSandboxOn = this.view.addonFields.sandboxAnalyzer.$uiToggle.value;
    const isAtsOn = isHyperDetectOn || isSandboxOn;

    if (this.view.dom.$advancedThreatSecurityToggle.value !== isAtsOn) {
      this.view.dom.$advancedThreatSecurityToggle.value = isAtsOn;
    }
  }

  private getXdrToggleableFeaturesValue() {
    return [
      this.view.addonFields.identityProviders.$uiToggle.value,
      this.view.addonFields.productivityApps.$uiToggle.value,
      this.view.addonFields.networkSensors.$uiToggle.value,
      this.view.addonFields.cloudWorkloads.$uiToggle.value,
    ];
  }

  private updateXDRToggleIdentityProviders(): void {
    let isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads;
    [isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads] =
      this.getXdrToggleableFeaturesValue();
    const isXDROn = isProductivityApps || isNetworkSensors || isCloudWorkloads;

    if (this.view.dom.$extendedDetectionAndResponseToggle.value !== isIdentityProviders && !isXDROn) {
      this.view.dom.$extendedDetectionAndResponseToggle.value = isIdentityProviders;
    }
  }

  private updateXDRToggleCloudWorkloads(): void {
    let isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads;
    [isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads] =
      this.getXdrToggleableFeaturesValue();
    const isXDROn = isIdentityProviders || isNetworkSensors || isProductivityApps;

    if (this.view.dom.$extendedDetectionAndResponseToggle.value !== isCloudWorkloads && !isXDROn) {
      this.view.dom.$extendedDetectionAndResponseToggle.value = isCloudWorkloads;
    }
  }

  private updateXDRToggleNetworkSensors(): void {
    let isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads;
    [isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads] =
      this.getXdrToggleableFeaturesValue();
    const isXDROn = isIdentityProviders || isProductivityApps || isCloudWorkloads;

    if (this.view.dom.$extendedDetectionAndResponseToggle.value !== isNetworkSensors && !isXDROn) {
      this.view.dom.$extendedDetectionAndResponseToggle.value = isNetworkSensors;
    }
  }

  private updateXDRToggleProductivityApps(): void {
    let isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads;
    [isIdentityProviders, isProductivityApps, isNetworkSensors, isCloudWorkloads] =
      this.getXdrToggleableFeaturesValue();
    const isXDROn = isIdentityProviders || isNetworkSensors || isCloudWorkloads;

    if (this.view.dom.$extendedDetectionAndResponseToggle.value !== isProductivityApps && !isXDROn) {
      this.view.dom.$extendedDetectionAndResponseToggle.value = isProductivityApps;
    }
  }

  private onIdentityProvidersToggle(): void {
    this.updateXDRToggleIdentityProviders();
  }

  private onProductivityAppsToggle(): void {
    this.updateXDRToggleProductivityApps();
  }

  private onNetworkSensors(): void {
    this.updateXDRToggleNetworkSensors();
  }

  private onIntegrityMonitorToggleChange(event: WebComponentEvent): void {
    if (this.formType !== AddonFormType.Resell && event.detail.checked) {
      this.view.dom.$imRetentionAddons.setEnabled(true);
      this.view.dom.$imRetentionAddons.setFormValue(this.view.imES7daysDummyFlag);
    } else {
      this.view.dom.$imRetentionAddons.setFormValue('');
      this.view.dom.$imRetentionAddons.setEnabled(false);
    }
  }

  private onIntegrityMonitorRetentionSelection(event: CustomEvent): void {
    if (this.usableProduct === MonthlyProductType.BitdefenderEdr) {
      return;
    }

    this.imES90daysStorageFormField.value = false;
    this.imES180daysStorageFormField.value = false;
    this.imES365daysStorageFormField.value = false;

    switch (event.detail.selectedValue) {
      case FeatureFlag.PRODUCT_FEATURE_IM_ES_90DAYS_STORAGE:
        this.imES90daysStorageFormField.value = true;
        return;

      case FeatureFlag.PRODUCT_FEATURE_IM_ES_180DAYS_STORAGE:
        this.imES180daysStorageFormField.value = true;
        return;

      case FeatureFlag.PRODUCT_FEATURE_IM_ES_365DAYS_STORAGE:
        this.imES365daysStorageFormField.value = true;
        return;
    }
  }

  private onCloudWorkloads(): void {
    this.updateXDRToggleCloudWorkloads();
  }

  private onEndpointDetectionAndResponseToggle(): void {
    const isEdrEnabled = this.view.addonFields.endpointDetectionAndResponse.$uiToggle.value;
    const isEdrReadOnly = this.view.addonFields.endpointDetectionAndResponse.$uiToggle.disabled;
    const isEdrTrial = (this.featuresInTrial ?? {})[FeatureFlag.PRODUCT_FEATURE_EDR_USABLE];
    if (isEdrEnabled) {
      this.view.dom.$advancedThreatSecurityToggle.value = true;
      this.view.dom.$advancedThreatSecurityToggle.disabled = true;

      this.view.addonFields.hyperDetect.$uiToggle.value = true;
      this.view.addonFields.hyperDetect.$uiToggle.disabled = true;

      this.view.addonFields.sandboxAnalyzer.$uiToggle.value = true;
      this.view.addonFields.sandboxAnalyzer.$uiToggle.disabled = true;

      if(isEdrTrial) {
        this.view.dom.$edrRetentionAddons.setEnabled(false);
        this.view.dom.$edrRetentionAddons.setFormValue(this.view.edrRetentionNoRetentionDummyFlag);
        return;
      }

      this.view.dom.$edrRetentionAddons.setEnabled(true);
      if (!isEdrReadOnly || this.view.dom.$edrRetentionAddons.getFormValue() === '') {
        this.view.dom.$edrRetentionAddons.setFormValue(this.view.edrRetentionNoRetentionDummyFlag);
      }

    } else {
      this.view.dom.$advancedThreatSecurityToggle.disabled = isEdrReadOnly;
      this.view.addonFields.hyperDetect.$uiToggle.disabled = isEdrReadOnly;
      this.view.addonFields.sandboxAnalyzer.$uiToggle.disabled = isEdrReadOnly;
      this.view.addonFields.identityProviders.$uiToggle.value = false;
      this.view.addonFields.productivityApps.$uiToggle.value = false;
      this.view.addonFields.networkSensors.$uiToggle.value = false;
      this.view.addonFields.cloudWorkloads.$uiToggle.value = false;

      this.view.dom.$edrRetentionAddons.setEnabled(false);
      this.view.dom.$edrRetentionAddons.setFormValue('');
    }
  }

  private onAddonToggle(addonField: AddonFieldComponents) {
    this.updateSingleAddonFieldComponents(addonField);

    if (this.view.allowedFeaturesToDelegateWithoutOwning.has(addonField.key)) {
      return;
    }

    // Update own use edr toggle if user enable xdr for resell
    if (this.isMainForm() || this.shouldUpdateEdrToggle(addonField)) {
      // Keep the other form synchronized
      this.dispatchToggleEvent(addonField.key, addonField.$uiToggle.value);
    }
  }

  private onExtendedDetectAndResponseToggle(): void {
    const value = this.view.dom.$extendedDetectionAndResponseToggle.value;
    const isIdentityProviders = this.view.addonFields.identityProviders.$uiToggle.value;
    const isProductivityApps = this.view.addonFields.productivityApps.$uiToggle.value;
    const isNetworkSensors = this.view.addonFields.networkSensors.$uiToggle.value;
    const isCloudWorkloads = this.view.addonFields.cloudWorkloads.$uiToggle.value;
    const shouldChangeGroup =
      !value || ((isIdentityProviders === isProductivityApps) === isNetworkSensors) === isCloudWorkloads;

    if (value && !this.view.addonFields.endpointDetectionAndResponse.$uiToggle.value) {
      this.view.addonFields.endpointDetectionAndResponse.$uiToggle.value = value;
    }

    if (shouldChangeGroup) {
      if (isIdentityProviders !== value) {
        this.view.addonFields.identityProviders.$uiToggle.value = value;
      }

      if (isProductivityApps !== value) {
        this.view.addonFields.productivityApps.$uiToggle.value = value;
      }

      if (isCloudWorkloads !== value) {
        this.view.addonFields.cloudWorkloads.$uiToggle.value = value;
      }

      if (isNetworkSensors !== value) {
        this.view.addonFields.networkSensors.$uiToggle.value = value;
      }
    }
  }

  /**
   * Dispatch an event which keeps addon forms (own use and resell) in sync.
   * @param addonKey
   * @param value
   */
  private dispatchToggleEvent(addonKey: string, value: boolean): void {
    this.component.dispatchEvent(
      new CustomEvent(AddonFormEvent.ToggleUpdate, {
        detail: {
          addonKey,
          value,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Check if the form must emmit events in order to keep the other form synchronized.
   *
   * @return boolean
   */
  private isMainForm(): boolean {
    // Own use is the main form for customers or for partners with Endpoint Security
    if (this.formType === AddonFormType.OwnUse) {
      return (
        this.childCompanyType === CompanyType.Customer || this.usableProduct === MonthlyProductType.EndpointSecurity
      );
    }

    // Resell form is the main form for partners with companion
    return this.childCompanyType === CompanyType.Partner && this.usableProduct === MonthlyProductType.BitdefenderEdr;
  }

  /**
   * When xdr for resell is enabled, we should update also edr for resell and own use
   * @param addonField
   * @private
   */
  private shouldUpdateEdrToggle(addonField: AddonFieldComponents) {
    return addonField.key === this.view.addonFields.endpointDetectionAndResponse.key && addonField.$uiToggle.value;
  }

  public hideAddons(features: FeatureFlag[]): void {
    this.setAddonsDefaultVisibility();
    this.view.hideFeatureAddons(features);
    this.setXDRToggle();
    this.hideXDRSensorsForResell();
  }

  public isAnyAddonVisible(): boolean {
    for (const addon in this.view.addonFields) {
      if (this.view.addonFields[addon]) {
        if (!isHidden(this.view.addonFields[addon].$uiToggle)) {
          return true;
        }
      }
    }
    return false;
  }

  public setCheckedValueForFeatureAddons(features: FeatureFlag[], checkedValue: boolean): void {
    this.view.setCheckedValueForFeatureAddons(features, checkedValue);
  }

  private setAddonsDefaultVisibility(): void {
    toggleVisibility(this.view.dom.$advancedThreatSecurityToggle, true);
    toggleVisibility(this.view.dom.$endpointDetectionAndResponseText, true);
    toggleVisibility(this.view.dom.$extendedDetectionAndResponseToggle, true);
    for (const addon in this.view.addonFields) {
      if (this.view.addonFields[addon]) {
        toggleVisibility(this.view.addonFields[addon].$uiToggle, this.isAddonAvailable(this.view.addonFields[addon]));
      }
    }
  }
}
